//
// # EditTextAnnotationDialog
//
// Command-palette style dialog for adding or editing a schematic text
// annotation. When editing, the input is pre-filled with the current text;
// submitting empty text removes the annotation.
//

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useWorkspace, useWorkspaceSnapshot } from "../context";

export interface EditTextAnnotationDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function EditTextAnnotationDialog({
  open,
  onClose,
}: EditTextAnnotationDialogProps) {
  const workspace = useWorkspace();
  const { textAnnotationDialog } = useWorkspaceSnapshot();

  // Form state
  const [text, setText] = useState("");

  // Reset the form when the dialog opens with new state.
  useEffect(() => {
    if (open && textAnnotationDialog) {
      setText(textAnnotationDialog.currentText);
    }
  }, [open, textAnnotationDialog?.currentText]);

  const isEditing = textAnnotationDialog?.target.kind === "existing";

  const handleSubmit = useCallback(() => {
    if (!textAnnotationDialog) return;

    const { designIdent, target } = textAnnotationDialog;
    const trimmed = text.trim();

    if (target.kind === "new") {
      // Don't create an empty annotation.
      if (trimmed !== "") {
        workspace.addTextAnnotation(designIdent, target.position, trimmed);
      }
    } else {
      // Editing: empty text removes the annotation.
      workspace.updateTextAnnotation(designIdent, target.elementId, trimmed);
    }

    onClose();
  }, [textAnnotationDialog, text, workspace, onClose]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [handleSubmit, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!open) return null;

  const title = isEditing ? "Edit Text" : "Add Text";
  const placeholder = isEditing
    ? "Enter text (leave empty to remove)"
    : "Enter text";
  const submitLabel = isEditing ? "Update" : "Add";

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">{title}</div>
        <input
          className="command-palette-input"
          type="text"
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          autoFocus
        />
        <div className="add-instance-footer">
          <span className="add-instance-hint">
            Enter to submit · Escape to cancel
          </span>
          <button
            className="add-instance-btn"
            onClick={handleSubmit}
            disabled={!textAnnotationDialog}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
